import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { FiArrowLeft, FiChevronDown } from "react-icons/fi";
import {
  openOtpModal,
  closeOtpModal,
} from "../../features/withdraw-otp/withdrawOtpSlice";
import { WithdrawOtpModal } from "../../components/Dashboard/withdraw-now/WithdrawOtpModal";
import { getCoinIcon } from "../../components/Dashboard/coins/CoinIcons";
import { useGetWalletQuery } from "../../features/wallet/walletSlice";
import {
  useGetCryptoPayoutQuoteMutation,
  useConfirmCryptoPayoutMutation,
  useConfirmCryptoPayoutWithOtpMutation,
  useListMyCryptoPayoutsQuery,
  PayoutAsset,
  PayoutStatus,
  QuoteResponse,
} from "../../features/cryptoPayouts/cryptoPayoutsSlice";
import { STATUS_LABEL, STATUS_CLASS } from "../../features/cryptoPayouts/status";
import { WITHDRAWAL_SOURCES, SOURCE_LABELS, WithdrawalSource, balanceFor, normalizeSource } from "../../features/withdraws/source";

// User-facing coins. USDT resolves to a network-specific backend asset.
const COINS: { key: string; icon: string; label: string; usesNetwork?: boolean }[] = [
  { key: "bitcoin", icon: "bitcoin", label: "Bitcoin (BTC)" },
  { key: "litecoin", icon: "litecoin", label: "Litecoin (LTC)" },
  { key: "dogecoin", icon: "dogecoin", label: "Dogecoin (DOGE)" },
  { key: "ethereum", icon: "ethereum", label: "Ethereum (ETH)" },
  { key: "usdt", icon: "usdt", label: "Tether (USDT)", usesNetwork: true },
];

const NETWORKS: { value: PayoutAsset; label: string }[] = [
  { value: "USDT_TRC20", label: "USDT (TRC-20 / Tron)" },
  { value: "USDT_ERC20", label: "USDT (ERC-20 / Ethereum)" },
];

const MIN_NAIRA = 10000;
const MAX_NAIRA = 1000000;

function resolveAsset(coinKey: string, network: PayoutAsset): PayoutAsset {
  if (coinKey === "usdt") return network;
  return ({ bitcoin: "BTC", litecoin: "LTC", dogecoin: "DOGE", ethereum: "ETH" } as const)[
    coinKey as "bitcoin" | "litecoin" | "dogecoin" | "ethereum"
  ];
}

const fmtUsd = (n: number) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtNaira = (n: number) => `₦${Number(n).toLocaleString()}`;

export default function CryptoPayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Which balance pays (Main / Reward / Referral) — same choice as the bank
  // withdraw form; prefilled from ?source= when the user came from there.
  const [searchParams] = useSearchParams();
  const [source, setSource] = useState<WithdrawalSource>(normalizeSource(searchParams.get("source")));
  const [coinKey, setCoinKey] = useState("bitcoin");
  const [network, setNetwork] = useState<PayoutAsset>("USDT_TRC20");
  const [coinOpen, setCoinOpen] = useState(false);
  const [destinationAddress, setDestinationAddress] = useState("");
  const [nairaAmount, setNairaAmount] = useState("");
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const { data: wallet } = useGetWalletQuery(undefined as any);
  const [getQuote, { isLoading: quoting }] = useGetCryptoPayoutQuoteMutation();
  const [confirmPayout, { isLoading: confirming }] = useConfirmCryptoPayoutMutation();
  const [confirmWithOtp] = useConfirmCryptoPayoutWithOtpMutation();
  const { data: payouts } = useListMyCryptoPayoutsQuery();
  // client-side pagination for the history list (6 per page)
  const PAYOUTS_PER_PAGE = 6;
  const [payoutPage, setPayoutPage] = useState(0);
  const payoutPageCount = Math.max(1, Math.ceil((payouts?.length ?? 0) / PAYOUTS_PER_PAGE));
  const pagedPayouts = [...(payouts ?? [])].slice(
    payoutPage * PAYOUTS_PER_PAGE,
    (payoutPage + 1) * PAYOUTS_PER_PAGE
  );

  const coin = COINS.find(c => c.key === coinKey)!;
  const asset = useMemo(() => resolveAsset(coinKey, network), [coinKey, network]);

  // Quote expiry countdown (backend quoteTtlSeconds default 120s).
  useEffect(() => {
    if (!quote || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [quote, secondsLeft]);

  const amountNum = Number(nairaAmount);
  const amountValid = amountNum >= MIN_NAIRA && amountNum <= MAX_NAIRA;
  const canQuote = destinationAddress.trim().length > 6 && amountValid && !quoting;

  const handleGetQuote = async () => {
    if (!canQuote) return;
    try {
      const res = await getQuote({
        asset,
        nairaAmount: amountNum,
        destinationAddress: destinationAddress.trim(),
        source,
      }).unwrap();
      setQuote(res);
      setSecondsLeft(120);
    } catch (err: any) {
      toast.error(err?.data?.message || "Could not fetch a quote. Try again.");
    }
  };

  const resetToForm = () => {
    setQuote(null);
    setSecondsLeft(0);
  };

  const onConfirmSuccess = () => {
    toast.success("Payout submitted! Track it below.");
    setDestinationAddress("");
    setNairaAmount("");
    resetToForm();
  };

  const handleConfirm = async () => {
    if (!quote) return;
    if (secondsLeft <= 0) {
      toast.error("Quote expired — please get a new quote.");
      return;
    }
    try {
      await confirmPayout({ quoteId: quote.quoteId }).unwrap();
      onConfirmSuccess();
    } catch (err: any) {
      // OTP challenge (money-moving parity with fiat withdrawal)
      if (
        err?.status === 401 &&
        err?.data?.message?.includes("OTP required") &&
        err?.data?.challengeId
      ) {
        dispatch(
          openOtpModal({
            challengeId: err.data.challengeId,
            ttlSeconds: err.data.instructions?.ttlSeconds || 300,
          })
        );
        toast.error("OTP verification required. Check your email.");
      } else {
        toast.error(err?.data?.message || "Could not confirm the payout.");
      }
    }
  };

  const handleOtpSubmit = async (challengeId: string, otpCode: string) => {
    if (!quote) return;
    try {
      await confirmWithOtp({ quoteId: quote.quoteId, challengeId, otpCode }).unwrap();
      dispatch(closeOtpModal());
      onConfirmSuccess();
    } catch (err: any) {
      toast.error(err?.data?.message || "Invalid or expired OTP.");
    }
  };

  return (
    <div className="container-lg font-dashboard py-6 text-white">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-white/70 hover:text-white mb-4"
      >
        <FiArrowLeft /> Back
      </button>

      <h1 className="text-2xl font-semibold mb-1">Quick Crypto Payout</h1>
      <p className="text-white/60 text-sm mb-6">
        Withdraw your Naira balance as crypto, sent on-chain to your wallet.
        {balanceFor(wallet, source) != null && (
          <> Available {SOURCE_LABELS[source].toLowerCase()} balance: <span className="text-white">{fmtNaira(balanceFor(wallet, source)!)}</span>.</>
        )}
      </p>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ── Left: quote form / review ── */}
        <div className="bg-black rounded-xl p-5 border border-primary/20">
          {!quote ? (
            <>
              {/* Pay from */}
              <label className="text-sm text-white/70">Pay from</label>
              <div className="grid grid-cols-3 gap-2 mt-1 mb-4">
                {WITHDRAWAL_SOURCES.map(src => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setSource(src)}
                    className={`form-input text-sm ${source === src ? "border-primary text-white" : "text-white/60"}`}
                  >
                    {SOURCE_LABELS[src]}
                    {balanceFor(wallet, src) != null && (
                      <span className="block text-[11px] text-white/40">{fmtNaira(balanceFor(wallet, src)!)}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Coin */}
              <label className="text-sm text-white/70">Select Coin</label>
              <div className="relative mt-1">
                <button
                  type="button"
                  className="form-input w-full flex items-center justify-between gap-2 text-left"
                  onClick={() => setCoinOpen(o => !o)}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6">{getCoinIcon(coin.icon)}</span>
                    <span className="truncate">{coin.label}</span>
                  </span>
                  <FiChevronDown className={`w-5 h-5 transition-transform ${coinOpen ? "rotate-180" : ""}`} />
                </button>
                {coinOpen && (
                  <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-primary/40 bg-gray-700 py-1 shadow-lg">
                    {COINS.map(c => (
                      <li
                        key={c.key}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer text-white/90 hover:bg-gray-600"
                        onClick={() => {
                          setCoinKey(c.key);
                          setCoinOpen(false);
                        }}
                      >
                        <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6">{getCoinIcon(c.icon)}</span>
                        <span className="truncate">{c.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Network (USDT only) */}
              {coin.usesNetwork && (
                <div className="mt-4">
                  <label className="text-sm text-white/70">Network</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {NETWORKS.map(n => (
                      <button
                        key={n.value}
                        type="button"
                        onClick={() => setNetwork(n.value)}
                        className={`form-input text-sm ${
                          network === n.value ? "border-primary text-white" : "text-white/60"
                        }`}
                      >
                        {n.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Destination */}
              <div className="mt-4">
                <label htmlFor="dest" className="text-sm text-white/70">Payout Address</label>
                <input
                  id="dest"
                  type="text"
                  placeholder={`Your ${coin.usesNetwork ? "USDT" : coin.label.split(" ")[0]} address`}
                  value={destinationAddress}
                  onChange={e => setDestinationAddress(e.target.value)}
                  className="form-input spin-button-none mt-1"
                />
                <p className="text-white/40 text-xs mt-1">
                  Double-check the address — on-chain payouts cannot be reversed.
                </p>
              </div>

              {/* Amount */}
              <div className="mt-4">
                <label htmlFor="amt" className="text-sm text-white/70">Amount (₦)</label>
                <input
                  id="amt"
                  type="number"
                  placeholder={`${MIN_NAIRA} – ${MAX_NAIRA}`}
                  value={nairaAmount}
                  onWheel={e => e.currentTarget.blur()}
                  onChange={e => setNairaAmount(e.target.value)}
                  className="form-input spin-button-none mt-1"
                />
                {nairaAmount !== "" && !amountValid && (
                  <p className="text-red-500 text-xs mt-1">
                    Enter between {fmtNaira(MIN_NAIRA)} and {fmtNaira(MAX_NAIRA)}.
                  </p>
                )}
              </div>

              <button
                onClick={handleGetQuote}
                disabled={!canQuote}
                className="btn-primary w-full mt-6 py-4 text-base font-medium disabled:opacity-50"
              >
                {quoting ? "Getting quote…" : "Get Quote"}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">Review Payout</h2>
                <span className={`text-xs px-2 py-1 rounded ${secondsLeft > 0 ? "bg-primary/20 text-primary" : "bg-red-500/20 text-red-400"}`}>
                  {secondsLeft > 0 ? `Expires in ${secondsLeft}s` : "Expired"}
                </span>
              </div>

              <dl className="mt-4 space-y-3 text-sm">
                <Row label="Coin" value={coin.usesNetwork ? NETWORKS.find(n => n.value === asset)?.label ?? asset : coin.label} />
                <Row label="To" value={<span className="break-all text-white/80">{destinationAddress}</span>} />
                <Row label="Paid from" value={SOURCE_LABELS[normalizeSource(quote.source)]} />
                <Row label="You withdraw" value={fmtNaira(quote.amountEnteredNaira)} />
                <Row label="Value (USD)" value={fmtUsd(quote.estimatedPayoutBeforeFeesUsd)} />
                <Row label="Network + service fee" value={fmtUsd(quote.estimatedFeesUsd)} />
                <div className="border-t border-white/10 pt-3">
                  <Row
                    label={<span className="text-white">You receive</span>}
                    value={
                      <span className="text-white font-semibold">
                        {quote.cryptoUnits}{" "}
                        {coin.usesNetwork ? "USDT" : coin.label.split(" ")[0]}
                        <span className="text-white/50 font-normal"> (~{fmtUsd(quote.finalAmountUsd)})</span>
                      </span>
                    }
                  />
                </div>
              </dl>

              <div className="flex gap-3 mt-6">
                <button onClick={resetToForm} className="form-input flex-1 py-4 text-white/80">
                  Edit
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={confirming || secondsLeft <= 0}
                  className="btn-primary flex-1 py-4 text-base font-medium disabled:opacity-50"
                >
                  {confirming ? "Confirming…" : "Confirm Payout"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Right: recent payouts ── */}
        <div className="bg-black rounded-xl p-5 border border-primary/20">
          <h2 className="text-lg font-medium mb-4">Recent Payouts</h2>
          {!payouts || payouts.length === 0 ? (
            <p className="text-white/50 text-sm">No crypto payouts yet.</p>
          ) : (
            <ul className="space-y-3">
              {pagedPayouts.map(p => (
                <li key={p._id}>
                  <Link
                    to={`/app/crypto-payout/${p._id}`}
                    className="flex items-center justify-between gap-3 border-b border-white/5 pb-3 hover:bg-white/5 rounded px-1 -mx-1 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">
                        {p.cryptoUnits} {p.asset.startsWith("USDT") ? "USDT" : p.asset}
                      </p>
                      <p className="text-xs text-white/50">
                        {fmtNaira(p.nairaAmount)} · {new Date(p.createdAt).toLocaleDateString()}
                      </p>
                      {(p.status === "awaiting_approval" || p.status === "manual_review") && (
                        <p className="text-xs text-blue-400/80 mt-0.5">Being reviewed by our team</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${STATUS_CLASS[p.status]}`}>
                      {STATUS_LABEL[p.status]}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {(payouts?.length ?? 0) > PAYOUTS_PER_PAGE && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <button
                className="px-3 py-1.5 rounded bg-white/5 text-white/70 disabled:opacity-30 hover:bg-white/10 transition-colors"
                disabled={payoutPage === 0}
                onClick={() => setPayoutPage(payoutPage - 1)}
              >
                ← Prev
              </button>
              <span className="text-white/40 text-xs">
                Page {payoutPage + 1} of {payoutPageCount}
              </span>
              <button
                className="px-3 py-1.5 rounded bg-white/5 text-white/70 disabled:opacity-30 hover:bg-white/10 transition-colors"
                disabled={payoutPage >= payoutPageCount - 1}
                onClick={() => setPayoutPage(payoutPage + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      <WithdrawOtpModal onOtpSubmit={handleOtpSubmit} />
    </div>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-white/60">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
